"use client";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import clsx from "clsx";
import { ArrowUpIcon } from "@/assets/icons";

interface ScrollToTopButtonProps {
  showAfter?: number; // Pixels to scroll before showing the button
  size?: "sm" | "md" | "lg";
  className?: string;
}

const ScrollToTopButton: React.FC<ScrollToTopButtonProps> = ({
  showAfter = 200,
  size = "md",
  className,
}) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => setVisible(window.scrollY > showAfter);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [showAfter]);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  const sizeClasses = {
    sm: "min-h-9 min-w-9 p-2",
    md: "min-h-11 min-w-11 p-3",
    lg: "min-h-12 min-w-12 p-3.5",
  };

  return (
    <motion.button
      type="button"
      onClick={scrollToTop}
      initial={{ opacity: 0, scale: 0.85 }}
      animate={visible ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.85 }}
      whileHover={{ scale: 1.08 }}
      whileTap={{ scale: 0.94 }}
      transition={{ duration: 0.35, ease: "easeInOut" }}
      aria-label="Scroll to top"
      className={clsx("scroll-btn group", sizeClasses[size], className)}
    >
      <span className="scroll-btn__sheen" aria-hidden />
      <ArrowUpIcon strokeWidth={2.25} className="scroll-btn__icon" />
    </motion.button>
  );
};

export default ScrollToTopButton;
